import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Download, Info, Pause, Play, ShoppingBag, X } from "lucide-react";
import { WatermarkOverlay } from "@/components/WatermarkOverlay";
import { useCart } from "@/context/CartContext";
import { formatPrice, slugifyName } from "@/lib/utils";

const SLIDE_MS = 5200;

export function Slideshow({ photos, event, index, onClose, onIndexChange }) {
  const [playing, setPlaying] = useState(true);
  const [showInfo, setShowInfo] = useState(false);
  const { addItem } = useCart();
  const open = index !== null && index >= 0 && photos.length > 0;
  const photo = open ? photos[index] : null;

  const go = useCallback((step) => {
    if (!photos.length) return;
    onIndexChange(((index ?? 0) + step + photos.length) % photos.length);
  }, [index, photos.length, onIndexChange]);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(event) {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") go(1);
      if (event.key === "ArrowLeft") go(-1);
      if (event.key === " ") { event.preventDefault(); setPlaying((value) => !value); }
      if (event.key.toLowerCase() === "i") setShowInfo((value) => !value);
    }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, go, onClose]);

  useEffect(() => {
    if (!open || !playing) return undefined;
    const timer = setTimeout(() => go(1), SLIDE_MS);
    return () => clearTimeout(timer);
  }, [open, playing, index, go]);

  useEffect(() => {
    if (!open || photos.length < 2) return;
    const next = photos[(index + 1) % photos.length];
    if (next) {
      const img = new Image();
      img.src = next.preview_url;
    }
  }, [open, index, photos]);

  if (!open) return null;

  return (
    <motion.div data-testid="gallery-slideshow" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} className="fixed inset-0 z-[80] flex flex-col bg-onyx/[0.985] backdrop-blur-xl" role="dialog" aria-label={`${event.title} slideshow`}>
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-4 sm:px-8">
        <div className="min-w-0">
          <p className="truncate font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{event.title}</p>
          <p data-testid="slideshow-counter" className="mt-1 font-mono text-xs tracking-[0.24em] text-zinc-500">{String(index + 1).padStart(2, "0")} / {String(photos.length).padStart(2, "0")}</p>
        </div>
        <button data-testid="slideshow-close-button" onClick={onClose} className="border border-white/15 p-3 text-zinc-300 transition-colors hover:border-gold/50 hover:text-gold" aria-label="Close slideshow">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="relative flex-1 overflow-hidden">
        <AnimatePresence mode="popLayout">
          <motion.div key={photo.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.8, ease: "easeInOut" }} className="absolute inset-0 flex items-center justify-center">
            <motion.img
              data-testid="slideshow-image"
              src={photo.preview_url}
              alt={`${photo.title} from ${event.title}`}
              initial={{ scale: 1.02, x: index % 2 === 0 ? "-1%" : "1%" }}
              animate={{ scale: 1.12, x: index % 2 === 0 ? "1%" : "-1%" }}
              transition={{ duration: SLIDE_MS / 1000 + 1.5, ease: "linear" }}
              className="max-h-full max-w-full object-contain"
            />
            <WatermarkOverlay />
          </motion.div>
        </AnimatePresence>

        <button data-testid="slideshow-prev-button" onClick={() => { setPlaying(false); go(-1); }} className="absolute left-4 top-1/2 -translate-y-1/2 border border-white/15 bg-black/50 p-4 text-white backdrop-blur transition-colors hover:border-gold/50 hover:text-gold" aria-label="Previous photo">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button data-testid="slideshow-next-button" onClick={() => { setPlaying(false); go(1); }} className="absolute right-4 top-1/2 -translate-y-1/2 border border-white/15 bg-black/50 p-4 text-white backdrop-blur transition-colors hover:border-gold/50 hover:text-gold" aria-label="Next photo">
          <ChevronRight className="h-5 w-5" />
        </button>

        <AnimatePresence>
          {showInfo && (
            <motion.aside data-testid="slideshow-info-panel" initial={{ x: 420, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 420, opacity: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} className="absolute bottom-24 right-4 top-20 w-[calc(100%-2rem)] max-w-sm overflow-y-auto border border-gold/20 bg-onyx/90 p-6 backdrop-blur-2xl sm:right-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Frame details</p>
              <h3 className="mt-3 font-display text-3xl font-light text-white">{photo.title}</h3>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                {photo.vehicle || ""}
                {photo.rider && <>{photo.vehicle ? " · " : ""}<Link data-testid="slideshow-rider-link" to={`/riders/${slugifyName(photo.rider)}`} className="text-gold transition-colors hover:text-titanium">{photo.rider}</Link></>}
                {!photo.vehicle && !photo.rider && event.category}
              </p>
              {Object.keys(photo.exif || {}).length > 0 && (
                <div className="mt-6 grid grid-cols-2 gap-px border border-white/10 bg-white/10 font-mono text-xs uppercase tracking-[0.14em]">
                  {Object.entries(photo.exif).map(([key, value]) => (
                    <div key={key} className="bg-carbon p-3"><span className="block text-zinc-600">{key}</span><span className="mt-1 block text-titanium">{value}</span></div>
                  ))}
                </div>
              )}
              <div className="mt-6 space-y-3">
                <button data-testid="slideshow-buy-photo-button" onClick={() => addItem({ item_type: "photo", item_id: photo.id, title: photo.title, price: photo.price })} className="flex h-12 w-full items-center justify-center gap-2 bg-gold font-mono text-[11px] uppercase tracking-[0.2em] text-onyx transition-colors hover:bg-titanium">
                  <ShoppingBag className="h-4 w-4" /> Buy this frame · ${formatPrice(photo.price)}
                </button>
                <button data-testid="slideshow-buy-album-button" onClick={() => addItem({ item_type: "album", item_id: event.id, title: event.title, price: event.album_price })} className="flex h-12 w-full items-center justify-center gap-2 border border-white/15 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors hover:bg-white hover:text-onyx">
                  <Download className="h-4 w-4" /> Full album · ${Number(event.album_price).toFixed(0)}
                </button>
              </div>
            </motion.aside>
          )}
        </AnimatePresence>
      </div>

      <div className="border-t border-white/10 px-4 py-4 sm:px-8">
        <div data-testid="slideshow-progress" className="mb-4 h-px w-full bg-white/10">
          {playing && <motion.div key={`${photo.id}-${playing}`} initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: SLIDE_MS / 1000, ease: "linear" }} className="h-full bg-gold" />}
        </div>
        <div className="flex items-center justify-between">
          <p className="hidden font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-600 md:block">Arrows navigate · Space plays · i for details · Esc closes</p>
          <div className="flex items-center gap-2">
            <button data-testid="slideshow-info-toggle" onClick={() => setShowInfo((value) => !value)} className={`border p-3 transition-colors ${showInfo ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-zinc-300 hover:border-gold/50 hover:text-gold"}`} aria-label="Toggle frame details">
              <Info className="h-4 w-4" />
            </button>
            <button data-testid="slideshow-play-toggle" onClick={() => setPlaying((value) => !value)} className="border border-white/15 p-3 text-zinc-300 transition-colors hover:border-gold/50 hover:text-gold" aria-label={playing ? "Pause slideshow" : "Play slideshow"}>
              {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
